/*
Copyright (C) 2020 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React, { FC, useCallback, useState } from 'react';
import { includes } from 'lodash';

import log from '@broadleaf/admin-components/dist/common/utils/log';
import {
  IMetadata,
  IMetadataAction
} from '@broadleaf/admin-components/dist/types/metadata';
import { IFulfillmentViewLocalState } from '@broadleaf/admin-components/dist/oms/components/FulfillmentView/fulfillment';
import { getEndpointByType } from '@broadleaf/admin-components/dist/metadata/utils/MetadataUtils';
import { request } from '@broadleaf/admin-components/dist/metadata/utils/request';
import useContextParams from '@broadleaf/admin-components/dist/oms/components/FulfillmentView/hooks/useContextParams';
import { queueFetch } from '@broadleaf/admin-components/dist/oms/hooks/useFufillmentState';
import RmFulfillmentAction from '../../components/RmFulfillmentAction';
import cx from 'classnames';
import { getActionStatus } from '@src/oms/utils/RmFulfillmentUtils';
import { getSecureAssetUrl } from '@src/utils/auth-utils';

const logger = log.getLogger(
  'oms.components.FulfillmentView.action-components.DownloadOrderInvoice'
);

export interface DownloadOrderInvoiceProps {
  actionDefinition: IMetadataAction;
  dispatch: Function;
  metadata: IMetadata;
  state: IFulfillmentViewLocalState;
}

const DownloadOrderInvoice: FC<DownloadOrderInvoiceProps> = props => {
  const { actionDefinition, dispatch, state, metadata } = props;
  const { attributes = {}, label } = actionDefinition;
  const { allowedStatuses = [] } = attributes as Record<string, any>;

  const downloadOrderInvoiceEndpoint = getEndpointByType(
    actionDefinition,
    'DOWNLOAD_ORDER_INVOICE'
  );

  const contextParams = useContextParams(metadata, state);

  const [loading, setLoading] = useState<boolean>(false);

  const handleClick = useCallback(async () => {
    try {
      setLoading(true);

      const { data: invoiceAsset } = await request(
        { method: 'post', ...downloadOrderInvoiceEndpoint },
        contextParams
      );

      let invoiceUrl = invoiceAsset?.contentUrl;

      invoiceUrl = await getSecureAssetUrl(invoiceUrl);

      if (invoiceUrl) {
        window.open(invoiceUrl, '_blank');
      }
    } finally {
      setLoading(false);
      dispatch(queueFetch());
    }
  }, [
    contextParams,
    dispatch,
    downloadOrderInvoiceEndpoint,
    state.data.orderFulfillmentNumber
  ]);

  const { isDisabled, actionTooltip } = getActionStatus(
    'DOWNLOAD_ORDER_INVOICE',
    state.data
  );

  if (!includes(allowedStatuses, state.data.status)) {
    return <></>;
  }

  const actionTextColor = 'gray';

  return (
    <div className={cx('tw-w-full')}>
      <RmFulfillmentAction
        disabled={state.isFetching || loading || isDisabled}
        color={actionTextColor}
        onClick={handleClick}
        tooltip={actionTooltip}
      >
        {label}
      </RmFulfillmentAction>
    </div>
  );
};

export default DownloadOrderInvoice;
export { DownloadOrderInvoice };
